import AsyncStorage from '@react-native-async-storage/async-storage';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { captureException } from '@/utils/sentry';
import { FocusSource, useComponentFocus } from '@/hooks/use-component-focus';
import {
  ItemsContext_VariantsPackFieldsFragment,
  VolumePromotionsContext_VolumePromotionFieldsFragment,
} from '@/types';
import {
  useGetPromotionsForRestaurant,
  VariantPackPromotionsMapType,
} from '@/hooks/use-get-promotions-for-restaurant';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { VolumePromotion } from '@/utils/volume-promotions-types';

export const VIEWED_PROMOTIONS_SET_KEY = 'viewedVolumePromotionsSet';

export interface VolumePromotionsContextType {
  hasNewPromotions: boolean;
  markPromotionsViewed: () => Promise<void>;
  promotions: VolumePromotion[];
  variantPackPromotionsMap: VariantPackPromotionsMapType;
}

export interface VolumePromotionWithVariantPacks {
  promotion: VolumePromotionsContext_VolumePromotionFieldsFragment;
  variantPacks: ItemsContext_VariantsPackFieldsFragment[];
}

const emptyContextValue: VolumePromotionsContextType = {
  hasNewPromotions: false,
  markPromotionsViewed: async () => {},
  promotions: [],
  variantPackPromotionsMap: {},
};

export const VolumePromotionsContext =
  createContext<VolumePromotionsContextType | null>(null);

export const useVolumePromotionsContext = () => {
  const context = useContext(VolumePromotionsContext);
  if (!context) {
    throw new Error(
      'useVolumePromotionsContext requires a VolumePromotionsContextProvider',
    );
  }

  return context;
};

interface Props {
  children?: React.ReactNode;
}

export const VolumePromotionsContextProvider: React.FC<Props> = ({
  children,
}) => {
  const [hasNewPromotions, setHasNewPromotions] = useState(false);
  const { data, promotions, refetch, variantPackPromotionsMap } =
    useGetPromotionsForRestaurant();
  const isOfflineModeActive = useOfflineModeActive();

  // Refetch whenever the app receives focus
  useComponentFocus({
    onFocus: useCallback(
      async (type: FocusSource) => {
        if (isOfflineModeActive) {
          return;
        }

        if (type === FocusSource.App) {
          try {
            await refetch();
          } catch (e) {
            // Do nothing
          }
        }
      },
      [isOfflineModeActive, refetch],
    ),
  });

  useEffect(() => {
    (async () => {
      const promotionUUIDs = (data?.getPromotionsForRestaurant || []).reduce<
        string[]
      >((result, p) => {
        // Consider promotions with a marketing tile
        if (p.promotion && !p.promotion.hide_marketing_tile) {
          result.push(p.promotion.uuid);
        }

        return result;
      }, []);

      if (!promotionUUIDs.length) {
        setHasNewPromotions(false);
        return;
      }

      // Check if there are any promotion UUIDs that are not already viewed
      const viewedPromotionsSet = await getViewedPromotionsSet();
      setHasNewPromotions(
        promotionUUIDs.some((uuid) => !viewedPromotionsSet.has(uuid)),
      );
    })();
  }, [data]);

  const value = useMemo(() => {
    return {
      hasNewPromotions,
      markPromotionsViewed: async () => {
        const viewedPromotionsSet = await getViewedPromotionsSet();
        for (const promotion of promotions) {
          viewedPromotionsSet.add(promotion.uuid);
        }

        await AsyncStorage.setItem(
          VIEWED_PROMOTIONS_SET_KEY,
          JSON.stringify([...viewedPromotionsSet]),
        );

        setHasNewPromotions(false);
      },
      promotions,
      variantPackPromotionsMap,
    };
  }, [hasNewPromotions, promotions, variantPackPromotionsMap]);

  return (
    <VolumePromotionsContext.Provider value={value}>
      {children}
    </VolumePromotionsContext.Provider>
  );
};

export const DummyVolumePromotionsContextProvider: React.FC<Props> = ({
  children,
}) => {
  return (
    <VolumePromotionsContext.Provider value={emptyContextValue}>
      {children}
    </VolumePromotionsContext.Provider>
  );
};

// Returns a set of viewed promotion UUIDs
const getViewedPromotionsSet = async (): Promise<Set<string>> => {
  try {
    const value = await AsyncStorage.getItem(VIEWED_PROMOTIONS_SET_KEY);
    if (value) {
      return new Set(JSON.parse(value));
    }
  } catch (e) {
    captureException(e);
  }

  return new Set();
};
